import axios, { AxiosError } from "axios";
import { config } from "@/config";
import { apiErrorMessage } from "./client";

/**
 * Member portal calls. Uses the member session token, not the admin token.
 * In vite dev, /pp-api is proxied to the Perk Point API.
 */
export const memberPortalApi = axios.create({
  baseURL: import.meta.env.DEV
    ? "/pp-api"
    : config.PERKPOINT_API_URL || "http://localhost:3000",
  headers: { "Content-Type": "application/json" },
});

const TOKEN_KEY = "memberPortalV2Token";

export function getMemberToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}

export function setMemberToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function clearMemberToken() {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

memberPortalApi.interceptors.request.use((request) => {
  const token = getMemberToken();
  if (token) request.headers.set("Authorization", `Bearer ${token}`);
  return request;
});

export function memberErrorMessage(error: unknown, fallback = "Something went wrong") {
  if (error instanceof AxiosError && error.response?.status === 401) {
    const code = (error.response.data as { error?: { code?: string } } | undefined)?.error?.code;
    if (code === "unauthorized") return "Please sign in again.";
  }
  return apiErrorMessage(error, fallback);
}

export type PortalAccount = {
  member_id: string;
  external_member_id: string;
  full_name: string;
  initials: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  gender: string | null;
  date_of_birth: string | null;
  enrolled_at: string;
  status: string;
  account_status: "Active" | "Blocked";
  transaction_count: number;
  qualifying_spend: number;
  saved: number;
  program: { id: string; name: string; currency: string; point_value: number };
  tier: { id: string; key: string; name: string; rank: number } | null;
  points: { available: number; pending: number; lifetime_earned: number; lifetime_redeemed: number };
  benefits: { name: string }[];
  progress: {
    metric: string;
    remaining_major: number;
    next_entry_major: number | null;
    next_tier_key: string | null;
    next_tier_name?: string | null;
    value_major: number;
  }[];
};

export type PortalTrip = {
  id: string;
  reference: string;
  kind: "stay" | "flight";
  status: "Upcoming" | "Completed" | "Cancelled";
  title: string;
  subtitle: string;
  image: string;
  start_date: string;
  end_date: string;
  detail: string;
  travellers: number;
  currency: string;
  total: number;
  cash_paid: number;
  points_used: number;
  points_earned: number;
  points_pending?: boolean;
  points_available_at?: string | null;
  saved: number;
};

export type PortalActivity = {
  items: {
    id: string;
    date: string;
    outlet: string;
    type: "Earned" | "Redeemed" | "Bonus" | "Adjustment";
    amount: number;
    currency: string;
    points: number;
    status: "Completed" | "Pending" | "Failed";
  }[];
  total: number;
  page: number;
  page_size: number;
  summary: { count: number; points_net: number; spend_total: number };
};

export const MemberPortalApi = {
  async requestOtp(body: {
    channel: "email" | "mobile";
    contact: string;
    program_id?: string;
    external_member_id?: string;
  }) {
    const { data } = await memberPortalApi.post<{
      request_id: string;
      expires_in: number;
      demo_code?: string;
    }>("/v1/portal/auth/otp", body);
    return data;
  },
  async verifyOtp(body: { request_id: string; code: string }) {
    const { data } = await memberPortalApi.post<{
      token: string;
      expires_at: string;
      session: { member_id: string; full_name: string; initials: string };
    }>("/v1/portal/auth/verify", body);
    return data;
  },
  async logout() {
    await memberPortalApi.post("/v1/portal/auth/logout");
  },
  async account() {
    const { data } = await memberPortalApi.get<PortalAccount>("/v1/me");
    return data;
  },
  async tiers() {
    const { data } = await memberPortalApi.get<{ id: string; key: string; name: string; rank: number; benefits: string[] }[]>(
      "/v1/me/tiers",
    );
    return data;
  },
  async transactions(params: { search?: string; type?: string; status?: string; page?: number; pageSize?: number }) {
    const { data } = await memberPortalApi.get<PortalActivity>("/v1/me/transactions", {
      params: {
        search: params.search || undefined,
        type: params.type && params.type !== "all" ? params.type : undefined,
        status: params.status && params.status !== "all" ? params.status : undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    return data;
  },
  async trips() {
    const { data } = await memberPortalApi.get<PortalTrip[]>("/v1/me/trips");
    return data;
  },
  async createTrip(body: {
    kind: "stay" | "flight";
    title: string;
    subtitle?: string;
    image?: string;
    start_date: string;
    end_date: string;
    detail?: string;
    travellers: number;
    total: number;
    points_to_use: number;
    guest: { name: string; email: string; mobile: string };
  }) {
    const { data } = await memberPortalApi.post<{
      trip: PortalTrip;
      tier_before: string | null;
      tier_after: string | null;
      points: { available: number; pending: number };
      earn: { points: number; state: "pending" | "available" | "none"; available_at: string | null };
    }>("/v1/me/trips", body);
    return data;
  },
  async cancelTrip(tripId: string) {
    const { data } = await memberPortalApi.post<{ trip: PortalTrip }>(`/v1/me/trips/${tripId}/cancel`);
    return data;
  },
};
